/**
 * Minimal IANA-timezone wall-clock helpers built on Intl.DateTimeFormat.
 *
 * No runtime dependency: `date-fns-tz` is available in this package but its
 * `fromZonedTime` reads wall-clock components via the system-local Date
 * getters, which silently corrupts wall times that fall inside the *system*
 * timezone's own DST gap. These helpers only ever use `Intl` with an explicit
 * `timeZone`, so results are independent of the machine's timezone.
 *
 * DST behavior:
 * - Nonexistent wall times (spring-forward gap, e.g. 02:30 on a US
 *   spring-forward day): `wallTimeToInstant` returns `null` — callers skip.
 * - Ambiguous wall times (fall-back, the repeated hour): the EARLIER instant
 *   (first occurrence, pre-transition offset) is returned.
 */

export interface LocalParts {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = Sunday … 6 = Saturday */
  dayOfWeek: number;
  /** Local calendar date as YYYY-MM-DD */
  dateKey: string;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      weekday: "short",
      hourCycle: "h23",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    getFormatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns the timezone if valid, otherwise warns and falls back to UTC.
 * Never throws — timezone strings come from user-supplied brand config.
 */
export function resolveTimeZone(timeZone: string): string {
  if (isValidTimeZone(timeZone)) return timeZone;
  console.warn(`[scheduling] Unknown timezone "${timeZone}" — falling back to UTC`);
  return "UTC";
}

/**
 * The viewer's own IANA timezone, from the browser's Intl runtime (falls back
 * to UTC if unavailable). This is the canonical source for all viewer-local
 * calendar/scheduling display — posts are stored as UTC instants and rendered
 * in whatever timezone this returns. The brand's scheduling-config timezone is
 * separate and governs only the server-side auto-suggest posting windows.
 */
export function viewerTimeZone(): string {
  return resolveTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

const pad = (n: number, width = 2): string => String(n).padStart(width, "0");

/** Local calendar/wall-clock parts of an absolute instant in `timeZone`. */
export function instantToLocalParts(date: Date, timeZone: string): LocalParts {
  const parts = getFormatter(timeZone).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";

  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const dayOfWeek = WEEKDAYS.indexOf(get("weekday"));

  return {
    year,
    month,
    day,
    hour,
    minute,
    dayOfWeek,
    dateKey: `${pad(year, 4)}-${pad(month)}-${pad(day)}`,
  };
}

/** Offset of `timeZone` at `date`, in ms (local wall time minus UTC). */
function tzOffsetAt(date: Date, timeZone: string): number {
  const p = instantToLocalParts(date, timeZone);
  const asUTC = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
  return asUTC - date.getTime();
}

/**
 * The UTC instant corresponding to the given wall-clock time in `timeZone`.
 *
 * Iterative offset trick: guess the offset at the wall time read as UTC,
 * adjust, then re-measure at the adjusted instant (the offset may have
 * changed across a DST boundary). Candidates are verified by round-tripping
 * back to local parts.
 *
 * Returns `null` if the wall time does not exist (spring-forward gap).
 * For ambiguous wall times (fall-back), returns the earlier instant.
 */
export function wallTimeToInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date | null {
  const wallAsUTC = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  // Probe one day on each side of the wall time to cover both negative and
  // positive UTC offsets. Using just the two obvious probes (wallAsUTC and
  // wallAsUTC - offset1) fails for positive-offset zones where both land
  // on the same side of the DST transition.
  const DAY = 86_400_000;
  const offsets = new Set([
    tzOffsetAt(new Date(wallAsUTC - DAY), timeZone),
    tzOffsetAt(new Date(wallAsUTC + DAY), timeZone),
  ]);
  const candidates = [...offsets].map((o) => wallAsUTC - o);

  // Prefer the earlier instant when both round-trip (fall-back ambiguity).
  for (const ts of candidates.sort((a, b) => a - b)) {
    const p = instantToLocalParts(new Date(ts), timeZone);
    if (
      p.year === year &&
      p.month === month &&
      p.day === day &&
      p.hour === hour &&
      p.minute === minute
    ) {
      return new Date(ts);
    }
  }

  // No instant maps to this wall time: it falls in a spring-forward gap.
  return null;
}
